import React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from '@perxis-auto/ui-kit/lib/components/Grid';
import { Typography } from '@perxis-auto/ui-kit/lib/components/Typography';

import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';

import { BenefitsTooltipProps } from './BenefitsTooltip.types';

import styles from './BenefitsTooltip.module.scss';

/**
 * Компонент контента тултипа, для отображения списка выгод.
 */
function BenefitsTooltip({ benefits, carPrice }: BenefitsTooltipProps) {
  const { t } = useTranslation();

  const benefitsConfig = useAppSelector(rootSelectors.config.benefitsSelector);
  const { showAmount } = benefitsConfig;

  const getPriceWithDiscount = (discount: number) => {
    const price = typeof carPrice === 'string' ? Number(carPrice) : carPrice;

    if (!price) {
      return 0;
    }

    return price - discount;
  };

  if (!benefits || benefits.length === 0) {
    return null;
  }

  return (
    <div className={styles.benefitsTooltip}>
      <Row gutter={[0, 12]}>
        <Col span={24}>
          <Typography.Text
            level="h4"
            color="darkest"
            className={styles.benefitsTooltipTitle}
          >
            {benefitsConfig.tooltipTitle}
          </Typography.Text>
        </Col>
        <Col span={24}>
          {/* Заголовок таблицы выгод */}
          <Row gutter={[0, 10]}>
            <Col span={24}>
              <Row>
                <Col
                  span={showAmount ? 12 : 18}
                  className={styles['benefitsTooltipTable--align-start']}
                >
                  <Typography.Text
                    level="h5"
                    color="dark-light"
                    className={styles.benefitsTooltipTableTitle}
                  >
                    {benefitsConfig.tooltipBenefitColumn}
                  </Typography.Text>
                </Col>
                {showAmount && (
                  <Col span={6} className={styles['benefitsTooltipTable--align-end']}>
                    <Typography.Text
                      level="h5"
                      color="dark-light"
                      className={styles.benefitsTooltipTableTitle}
                    >
                      {benefitsConfig.tooltipDiscountColumn}
                    </Typography.Text>
                  </Col>
                )}
                <Col span={6} className={styles['benefitsTooltipTable--align-end']}>
                  <Typography.Text
                    level="h5"
                    color="dark-light"
                    className={styles.benefitsTooltipTableTitle}
                  >
                    {benefitsConfig.tooltipPriceColumn}
                  </Typography.Text>
                </Col>
              </Row>
            </Col>

            {/* Тело таблицы выгод */}
            {benefits.map(benefit => (
              <Col key={benefit.id} span={24}>
                <Row>
                  <Col
                    span={showAmount ? 12 : 18}
                    className={styles['benefitsTooltipTable--align-start']}
                  >
                    <Typography.Text
                      level="super-small"
                      color="dark"
                      className={styles.benefitsTooltipTableLabel}
                    >
                      {benefit.displayName}
                    </Typography.Text>
                  </Col>
                  {showAmount && (
                    <Col span={6} className={styles['benefitsTooltipTable--align-end']}>
                      <Typography.Text
                        level="super-small"
                        color="darkest"
                        className={styles.benefitsTooltipTablePrice}
                      >
                        {t('currency', {
                          value: benefit.value,
                          style: 'decimal',
                        })}
                      </Typography.Text>
                    </Col>
                  )}
                  <Col span={6} className={styles['benefitsTooltipTable--align-end']}>
                    <Typography.Text
                      level="super-small"
                      color="darkest"
                      className={styles.benefitsTooltipTablePrice}
                    >
                      {t('currency', {
                        value: getPriceWithDiscount(benefit.value),
                        style: 'decimal',
                      })}
                    </Typography.Text>
                  </Col>
                </Row>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </div>
  );
}

export default BenefitsTooltip;
